import { useMediaQuery } from '@common/hooks';
import { SidebarProvider } from '@ntconsult/aluma-react';
import { useUIStore } from '@store/uiStore';
import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AppHeader } from './AppHeader';
import { AppSidebar } from './AppSidebar';

// Mesmo breakpoint que o SidebarProvider do Aluma usa por padrão (768).
const MOBILE_QUERY = '(max-width: 767px)';

export function Layout() {
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen);
  const isMobile = useMediaQuery(MOBILE_QUERY);
  // Em mobile a barra é um overlay: abre por botão, nunca persiste aberta e fecha ao navegar.
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const wasOpen = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fecha a cada mudança de rota
  useEffect(() => setMobileOpen(false), [pathname]);

  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMobileOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  // Foco: entra no overlay ao abrir e volta ao botão do cabeçalho ao fechar.
  useEffect(() => {
    if (mobileOpen) {
      wasOpen.current = true;
      document
        .querySelector<HTMLElement>('aside[role=dialog] a, aside[role=dialog] button')
        ?.focus();
    } else if (wasOpen.current) {
      wasOpen.current = false;
      document.querySelector<HTMLElement>('header button[aria-controls]')?.focus();
    }
  }, [mobileOpen]);

  return (
    <SidebarProvider
      collapsible={isMobile ? 'offcanvas' : 'icon'}
      open={isMobile ? mobileOpen : sidebarOpen}
      onOpenChange={isMobile ? setMobileOpen : setSidebarOpen}
    >
      <div className="flex h-dvh w-full">
        <AppSidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <AppHeader />
          {/* `relative`: os `sr-only` (absolute) de rádios, checkboxes e FormField ficam contidos no main; sem
              isso o containing block é a página, que passa a rolar e deixa um retângulo em branco. */}
          <main className="relative flex-1 min-h-0 overflow-y-auto p-4 md:p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
